import { useForm } from "react-hook-form";
import {
  FormError,
  Highlight,
  InputHeader,
  Spacer,
  StyledInput,
} from "../../styles/CommonElements";
import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import RevealPassword from "../RevealPassword";
import styled from "styled-components";
import { notification } from "antd";
import useLoginMutation from "../../lib/useLoginMutation";
import Button from "../Button";
import { Link } from "react-router-dom";
import { DIALOG_NAME, useOpenParamDialog } from "../../helpers/useDialogSearchParam";
import ForgotPassword from "./ForgotPassword";
import { APP_NAME } from "../../helpers/constants";
import SolvTaxLogo from "../general/Logo";
import { trackActivity } from "../../lib/siteActivity";

const LoginModal = ({ isOpen, onClose }) => {
  const [isPasswordRevealed, setPasswordRevealed] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const passwordRef = useRef();
  const openWaitlist = useOpenParamDialog(DIALOG_NAME.waitlist);

  const [api, contextHolder] = notification.useNotification();

  const { login, loginPending, loginError } = useLoginMutation({
    api,
  });

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty, isValid, errors },
  } = useForm({
    mode: "onTouched",
  });

  const onSubmit = (data) => {
    login(data, {
      onSuccess: () => {
        reset();
        onClose();
      },
      onError: () => {
        reset({}, { keepValues: true });
      },
    });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
      setForgotPassword(false);
      trackActivity({ event: "login_opened", path: window.location.pathname });
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    setForgotPassword(false);
    onClose();
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {contextHolder}
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
        onClick={handleBackdropClick}
        onKeyDown={(event) => {
          if (event.key === "Escape") handleClose();
        }}
      >
        <div className="relative w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-8 flex flex-col max-h-[92vh] overflow-hidden">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 md:top-6 md:right-6 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500/40 z-10"
            aria-label="Close login modal"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="overflow-y-auto flex-1 min-h-0 scrollbar-thin">
            <LogoWrapper>
              <SolvTaxLogo color="#000000" linkable={false} compact />
            </LogoWrapper>
            <Spacer $marginTop="0.5rem" />
            <ModalTitle id="login-modal-title">
              {!forgotPassword ? `Welcome to ${APP_NAME}` : "Reset your password"}
            </ModalTitle>
            <Spacer $marginTop="0.5rem" />
            {!forgotPassword && (
              <>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <InputHeader htmlFor="email">Email</InputHeader>
                  <Spacer $marginTop="0.25rem" />
                  <StyledInput
                    id="email"
                    placeholder="email@email.com"
                    $borderRadius="0px"
                    type="email"
                    {...register("email", {
                      required: "Please enter your email address",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {errors.email && <FormError>{errors.email.message}</FormError>}
                  <Spacer $marginTop="1rem" />

                  <InputHeader htmlFor="password">Password</InputHeader>
                  <Spacer $marginTop="0.25rem" />
                  <div style={{ position: "relative" }}>
                    <StyledInput
                      id="password"
                      placeholder="Enter Password"
                      $borderRadius="0px"
                      type={isPasswordRevealed ? "text" : "password"}
                      ref={passwordRef}
                      {...register("password", {
                        required: "Please enter your password",
                      })}
                    />
                    <RevealPassword
                      isRevealed={isPasswordRevealed}
                      toggleRevealed={() => setPasswordRevealed(!isPasswordRevealed)}
                      passwordRef={passwordRef}
                    />
                  </div>
                  {errors.password && <FormError>{errors.password.message}</FormError>}
                  <ForgotPasswordSection>
                    <Highlight
                      onClick={() => {
                        setForgotPassword(true);
                      }}
                    >
                      Forgot password?
                    </Highlight>
                  </ForgotPasswordSection>

                  <Spacer $marginTop="0.5rem" />

                  {loginError !== "" && <LoginError>{loginError}</LoginError>}

                  <Spacer $marginTop="1rem" />

                  <Button
                    type="submit"
                    size="large"
                    disabled={!isDirty || !isValid || loginPending}
                  >
                    Log In
                  </Button>
                </form>
                <Spacer $marginTop="1rem" />
                <Terms>
                  By continuing, you agree to our{" "}
                  <Link to="/terms-of-use" target="_blank">
                    <Highlight>Terms of Service</Highlight>
                  </Link>
                  .
                </Terms>
                <Spacer $marginTop="1rem" />
                <SignUpCta>
                  Don&apos;t have an account?{" "}
                  <SignUpLink
                    type="button"
                    onClick={() => {
                      handleClose();
                      openWaitlist();
                    }}
                  >
                    Sign Up
                  </SignUpLink>
                </SignUpCta>

                <Spacer $marginTop="0.25rem" />
              </>
            )}
            {forgotPassword && <ForgotPassword email={getValues("email")} />}
            <Spacer $marginTop="0.5rem" />
          </div>
        </div>
      </div>
    </>
  );
};

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 0.5rem;
  width: 100%;
`;

const ModalTitle = styled.div`
  color: var(--color-text);
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
`;

const ForgotPasswordSection = styled.div`
  text-align: right;
  width: 100%;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
  letter-spacing: 0.2px;
  margin-top: 12px;

  span {
    cursor: pointer;
  }
`;

const LoginError = styled.div`
  font-family: var(--font-secondary);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-align: left;
  color: var(--color-red);
`;

const SignUpCta = styled.div`
  color: var(--color-text);
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;
`;

const SignUpLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #22d3ee;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #67e8f9;
  }
`;

const Terms = styled.p`
  color: #777e91;
  text-align: center;
  font-size: 0.625rem;
  font-weight: 400;
`;

export default LoginModal;
